'use client';

import { FormatPrice } from '@/components/ui/FormatPrice';
import type { Location, MediaResponseDto, Property, PropertyAmenity, Valuation } from '@/lib/api/clients/properties';
import type { Listing } from '@/lib/api/clients/listings';
import {
  PropertyVirtualTour,
  PropertyVideos,
  PropertyFloorPlans,
  PropertyDocuments,
  groupMediaByContext,
} from '@/components/properties/PropertyMedia';
import { PropertyDetailTracker } from '@/components/properties/PropertyDetailTracker';
import { PropertySpecifications } from '@/components/properties/PropertySpecifications';
import { PropertyValuations } from '@/components/properties/PropertyValuations';
import { WishlistButton } from '@/components/customer/WishlistButton';
import { AgentMeetingModal } from '@/components/agents/AgentMeetingModal';
import { PropertyEnquiryModal } from '@/components/properties/PropertyEnquiryModal';
import { useTenantOptional } from '@/contexts/TenantContext';
import { analyticsEvents } from '@/components/analytics/GoogleAnalytics';
import {
  StonePropertyAmenities,
  StonePropertyGallery,
  StonePropertyLocation,
  StoneCinematicHeroCarousel,
  detectRegion,
  getRegionFlag,
  regionNames,
  type RegionType,
} from '@routeroyal/theme-stone-investment';
import { MarkdownRenderer } from '@routeroyal/ui-core';
import { AnimatePresence } from 'framer-motion';
import { ArrowLeft, Bath, Bed, Calendar, MapPin, Maximize, MessageCircle, Phone, Mail, Share2, User } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { useState } from 'react';

type Props = {
  property: Property;
  media: MediaResponseDto[];
  amenities: PropertyAmenity[];
  valuations?: Valuation[];
  listing?: Listing | null;
  price: number | null;
  currency: string;
  locale: string;
  messages: Record<string, unknown>;
  contact: { email?: string; phone?: string; whatsapp?: string };
  location?: Location | null | undefined;
  basePath?: string;
  listingAgent?: {
    id: string;
    name: string;
    email: string;
    avatar: string | null;
    jobTitle: string | null;
  } | null;
};

function detectPropertyRegion(location: Location | null | undefined, propertyName?: string): RegionType {
  if (!location) {
    return detectRegion(propertyName || '');
  }

  const locationParts = [
    location.country,
    location.countryCode,
    location.region,
    location.administrativeAreaName,
    location.city,
    location.district,
    location.suburb,
    location.neighbourhood,
  ].filter(Boolean);

  const locationString = locationParts.join(' ');
  const detectedRegion = detectRegion(locationString);

  if (detectedRegion === 'unknown' && propertyName) {
    return detectRegion(propertyName);
  }

  return detectedRegion;
}

/**
 * Stone Investment Property Detail Content
 *
 * Full-featured property detail page with Stone Investment's luxury aesthetic.
 * Includes all sections: hero, features, description, listing details,
 * specifications, amenities, virtual tours, videos, floor plans, gallery,
 * valuations, documents, location, and a rich contact card.
 */
export function PropertyDetailContentStone({
  property,
  media,
  amenities,
  valuations = [],
  listing,
  price,
  currency,
  locale,
  messages,
  contact,
  location,
  listingAgent,
}: Props) {
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [showEnquiryModal, setShowEnquiryModal] = useState(false);
  const tenant = useTenantOptional();

  const handleShare = () => {
    analyticsEvents.shareContent('property', property.id ?? '', 'share_button');
    if (navigator.share) {
      navigator.share({
        title: property.name,
        url: window.location.href,
      }).catch(() => {
        navigator.clipboard.writeText(window.location.href);
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
    }
  };

  const pd = messages.propertyDetail as Record<string, unknown> | undefined;
  const pdKeyFeatures = pd?.keyFeatures as Record<string, string> | undefined;
  const pdContact = pd?.contact as Record<string, string> | undefined;
  const pdLocation = pd?.location as Record<string, string> | undefined;
  const pdGallery = pd?.gallery as Record<string, string> | undefined;
  const pdAmenities = pd?.amenities as Record<string, string> | undefined;
  const pdSpecifications = pd?.propertyDetails as Record<string, string> | undefined;
  const pdListing = pd?.listingDetails as Record<string, string> | undefined;

  const detectedRegion = detectPropertyRegion(location, property.name);
  void (detectedRegion === 'provence');
  const regionFlag = getRegionFlag(detectedRegion);
  const regionName =
    regionNames[detectedRegion]?.[locale as 'en' | 'fr'] ||
    location?.region ||
    location?.country ||
    'Location';

  const locationLabel =
    [location?.city, location?.country].filter(Boolean).join(', ') || null;

  // Group media by context
  const mediaByContext = property.mediaLinks
    ? groupMediaByContext(property.mediaLinks)
    : {
        PRIMARY_HERO: [],
        GALLERY: [],
        FLOOR_PLAN: [],
        VIDEO: [],
        VIRTUAL_TOUR: [],
        DOCUMENT: [],
        OTHER: [],
      };

  const floorPlansFromLinks = (mediaByContext.FLOOR_PLAN || []).map((m) => m.media);

  // Build deduplicated image set for gallery
  const heroImages = (() => {
    const seen = new Set<string>();
    const result: MediaResponseDto[] = [];

    const addIfNew = (item: MediaResponseDto) => {
      const key = item.id || item.url;
      if (key && !seen.has(key)) {
        seen.add(key);
        if (item.url) seen.add(item.url);
        result.push(item);
      }
    };

    for (const m of media) {
      if (m.mediaType === 'VIDEO' || m.mimeType?.startsWith('video/')) continue;
      addIfNew(m);
    }

    for (const link of mediaByContext.GALLERY || []) {
      if (link.media) {
        addIfNew(link.media);
      }
    }

    return result;
  })();

  const propertyAddress =
    location?.addressLine1 ||
    [location?.streetNumber, location?.streetName].filter(Boolean).join(' ') ||
    location?.name ||
    '';
  const propertyCity = location?.city || location?.suburb || '';
  const propertyCountry = location?.country || '';
  const whatsappNumber = contact.whatsapp || contact.phone || '';

  // Build hero carousel slides: primary image first, then gallery images, then videos
  const carouselImages = (() => {
    const seen = new Set<string>();
    const result: { url: string; alt?: string | undefined; videoUrl?: string | undefined; mimeType?: string | undefined }[] = [];

    const addIfNew = (url: string, alt?: string, videoUrl?: string, mimeType?: string) => {
      const key = videoUrl || url;
      if (key && !seen.has(key)) {
        seen.add(key);
        result.push({ url, alt, videoUrl, mimeType });
      }
    };

    if (property.heroMedia?.url) addIfNew(property.heroMedia.url, property.name);
    if (property.primaryImageUrl) addIfNew(property.primaryImageUrl, property.name);

    for (const m of media) {
      if (m.mediaType === 'VIDEO' || m.mimeType?.startsWith('video/')) continue;
      if (m.url) addIfNew(m.url, `${property.name} - ${m.title || ''}`);
    }

    for (const link of mediaByContext.GALLERY || []) {
      if (link.media?.url) addIfNew(link.media.url, `${property.name} - gallery`);
    }

    // Prefer stored video posters, with the property hero as a fallback
    for (const link of mediaByContext.VIDEO || []) {
      if (!link.media?.url) continue;
      const poster =
        link.media.thumbnailUrl ||
        property.primaryImageUrl ||
        '';
      addIfNew(poster, link.contextualTitle || `${property.name} - video`, link.media.url, link.media.mimeType);
    }

    return result.slice(0, 12);
  })();

  return (
    <main className="min-h-screen bg-stone-50">
      <PropertyDetailTracker
        propertyId={property.id ?? ''}
        propertyName={property.name}
        price={price}
        currency={currency}
        {...(listing?.id ? { listingId: listing.id } : {})}
        {...(listing?.listingType ? { listingType: listing.listingType } : {})}
        bedrooms={property.bedrooms}
        bathrooms={property.bathrooms}
        location={locationLabel}
        {...(listingAgent?.name ? { agentName: listingAgent.name } : {})}
      />

      {/* Cinematic Hero Carousel */}
      <StoneCinematicHeroCarousel images={carouselImages}>
        <div className="absolute bottom-0 left-0 right-0 p-8 md:p-16 z-30">
          <div className="max-w-4xl">
            <p className="text-xs uppercase tracking-widest text-stone-300 mb-4 font-sans">
              {regionFlag} {regionName}
            </p>
            <p className="font-serif text-4xl md:text-6xl lg:text-7xl text-white leading-tight" aria-hidden="true">
              {property.name}
            </p>
            {price && (
              <div className="mt-6 text-2xl text-white font-sans font-light">
                <FormatPrice
                  amount={price}
                  currency={currency}
                  size="xl"
                  className="text-white"
                />
              </div>
            )}
          </div>
        </div>
      </StoneCinematicHeroCarousel>

      {/* Back Link */}
      <div className="container mx-auto px-6 md:px-12 pt-8">
        <Link
          href={`/${locale}/properties`}
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-stone-500 hover:text-stone-900 transition-colors font-sans"
        >
          <ArrowLeft size={14} />
          {(pd?.backToCollection as string) || 'Back to Collection'}
        </Link>
      </div>

      {/* Property Title, Price & Location */}
      <div className="container mx-auto px-6 md:px-12 py-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 pb-8 border-b border-stone-200">
          <div className="space-y-3">
            <p className="text-xs uppercase tracking-[0.3em] text-stone-500 font-sans">
              {regionFlag} {regionName}
              {locationLabel && ` \u2014 ${locationLabel}`}
            </p>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-stone-900 leading-tight">
              {property.name}
            </h1>
            {property.headline && property.headline !== property.name && (
              <p className="font-serif text-xl text-stone-600 italic">
                {property.headline}
              </p>
            )}
          </div>
          <div className="shrink-0">
            {price ? (
              <FormatPrice
                amount={price}
                currency={currency}
                size="xl"
                className="text-stone-900! font-serif text-3xl md:text-4xl"
              />
            ) : (
              <span className="font-serif text-2xl text-stone-500 italic">
                {(pd?.priceOnRequest as string) || 'Price on request'}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 md:px-12 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
          {/* Left Column - Property Details */}
          <div className="lg:col-span-2 space-y-12">
            {/* Key Features */}
            <div
              data-track-section="features"
              className="grid grid-cols-2 md:grid-cols-4 gap-6 pb-12 border-b border-stone-200"
            >
              {property.bedrooms && (
                <div className="text-center">
                  <Bed className="w-6 h-6 mx-auto mb-2 text-stone-400" />
                  <p className="text-2xl font-serif text-stone-900">
                    {property.bedrooms}
                  </p>
                  <p className="text-xs uppercase tracking-widest text-stone-500 font-sans">
                    {pdKeyFeatures?.bedrooms || 'Bedrooms'}
                  </p>
                </div>
              )}
              {property.bathrooms && (
                <div className="text-center">
                  <Bath className="w-6 h-6 mx-auto mb-2 text-stone-400" />
                  <p className="text-2xl font-serif text-stone-900">
                    {property.bathrooms}
                  </p>
                  <p className="text-xs uppercase tracking-widest text-stone-500 font-sans">
                    {pdKeyFeatures?.bathrooms || 'Bathrooms'}
                  </p>
                </div>
              )}
              {property.totalFloorArea && (
                <div className="text-center">
                  <Maximize className="w-6 h-6 mx-auto mb-2 text-stone-400" />
                  <p className="text-2xl font-serif text-stone-900">
                    {property.totalFloorArea.toLocaleString()}{' '}
                    {property.totalFloorAreaUnit?.toLowerCase() || 'm²'}
                  </p>
                  <p className="text-xs uppercase tracking-widest text-stone-500 font-sans">
                    {pdKeyFeatures?.area || 'Floor Area'}
                  </p>
                </div>
              )}
              {property.landArea && (
                <div className="text-center">
                  <Maximize className="w-6 h-6 mx-auto mb-2 text-stone-400" />
                  <p className="text-2xl font-serif text-stone-900">
                    {property.landArea.toLocaleString()}{' '}
                    {property.landAreaUnit?.toLowerCase() || 'm²'}
                  </p>
                  <p className="text-xs uppercase tracking-widest text-stone-500 font-sans">
                    {pdKeyFeatures?.landArea || 'Land Area'}
                  </p>
                </div>
              )}
              <div className="text-center">
                <MapPin className="w-6 h-6 mx-auto mb-2 text-stone-400" />
                <p className="text-lg font-serif text-stone-900">
                  {regionFlag} {location?.city || regionName}
                </p>
                <p className="text-xs uppercase tracking-widest text-stone-500 font-sans">
                  {pdKeyFeatures?.region || 'Region'}
                </p>
              </div>
            </div>

            {/* Additional Property Details */}
            {(property.yearBuilt || property.parkingSpaces || property.storeys) && (
              <div className="grid grid-cols-3 md:grid-cols-6 gap-4 pb-12 border-b border-stone-200">
                {property.yearBuilt && (
                  <div className="text-center">
                    <p className="text-lg font-serif text-stone-900">
                      {property.yearBuilt}
                    </p>
                    <p className="text-xs uppercase tracking-widest text-stone-500 font-sans">
                      {pdKeyFeatures?.yearBuilt || 'Year Built'}
                    </p>
                  </div>
                )}
                {property.yearRenovated && (
                  <div className="text-center">
                    <p className="text-lg font-serif text-stone-900">
                      {property.yearRenovated}
                    </p>
                    <p className="text-xs uppercase tracking-widest text-stone-500 font-sans">
                      {pdKeyFeatures?.yearRenovated || 'Renovated'}
                    </p>
                  </div>
                )}
                {(property.parkingSpaces || property.garageCount) && (
                  <div className="text-center">
                    <p className="text-lg font-serif text-stone-900">
                      {property.parkingSpaces || property.garageCount}
                    </p>
                    <p className="text-xs uppercase tracking-widest text-stone-500 font-sans">
                      {pdKeyFeatures?.parking || 'Parking'}
                    </p>
                  </div>
                )}
                {property.storeys && (
                  <div className="text-center">
                    <p className="text-lg font-serif text-stone-900">
                      {property.storeys}
                    </p>
                    <p className="text-xs uppercase tracking-widest text-stone-500 font-sans">
                      {pdKeyFeatures?.storeys || 'Storeys'}
                    </p>
                  </div>
                )}
                {property.floorNumber && (
                  <div className="text-center">
                    <p className="text-lg font-serif text-stone-900">
                      {property.floorNumber}
                    </p>
                    <p className="text-xs uppercase tracking-widest text-stone-500 font-sans">
                      {pdKeyFeatures?.floorNumber || 'Floor'}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Description */}
            <div data-track-section="overview">
              <h2 className="font-serif text-3xl text-stone-900 mb-6">
                {(pd?.aboutProperty as string) || 'About This Property'}
              </h2>
              <MarkdownRenderer
                content={
                  property.description ||
                  'A stunning property in our exclusive collection.'
                }
                theme="stone"
                className="font-sans font-light"
              />
            </div>

            {/* Listing Details (auction, rental) */}
            {listing &&
              (listing.auctionDetails ||
                listing.rentalDetails ||
                listing.availableDate) && (
                <>
                  <div className="border-t border-stone-200" />
                  <div>
                    <h2 className="font-serif text-3xl text-stone-900 mb-6">
                      {listing.auctionDetails
                        ? (pdListing?.auctionDetails || 'Auction Details')
                        : listing.rentalDetails
                          ? (pdListing?.rentalDetails || 'Rental Details')
                          : (pdListing?.listingDetails || 'Listing Details')}
                    </h2>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {listing.auctionDetails?.auctionDate && (
                        <div className="p-5 border border-stone-200 bg-white">
                          <p className="text-xs uppercase tracking-widest text-stone-500 font-sans mb-2">
                            {pdListing?.auctionDate || 'Auction Date'}
                          </p>
                          <p className="text-base font-serif text-stone-900">
                            {new Date(
                              listing.auctionDetails.auctionDate,
                            ).toLocaleDateString(locale, { dateStyle: 'long' })}
                          </p>
                        </div>
                      )}
                      {listing.auctionDetails?.auctionType && (
                        <div className="p-5 border border-stone-200 bg-white">
                          <p className="text-xs uppercase tracking-widest text-stone-500 font-sans mb-2">
                            {pdListing?.auctionType || 'Auction Type'}
                          </p>
                          <p className="text-base font-serif text-stone-900 capitalize">
                            {listing.auctionDetails.auctionType
                              .toLowerCase()
                              .replace(/_/g, ' ')}
                          </p>
                        </div>
                      )}
                      {listing.auctionDetails?.auctionLocation && (
                        <div className="p-5 border border-stone-200 bg-white">
                          <p className="text-xs uppercase tracking-widest text-stone-500 font-sans mb-2">
                            {pdListing?.auctionLocation || 'Auction Location'}
                          </p>
                          <p className="text-base font-serif text-stone-900">
                            {listing.auctionDetails.auctionLocation}
                          </p>
                        </div>
                      )}
                      {listing.rentalDetails?.furnishedStatus && (
                        <div className="p-5 border border-stone-200 bg-white">
                          <p className="text-xs uppercase tracking-widest text-stone-500 font-sans mb-2">
                            {pdListing?.furnished || 'Furnished'}
                          </p>
                          <p className="text-base font-serif text-stone-900 capitalize">
                            {listing.rentalDetails.furnishedStatus
                              .toLowerCase()
                              .replace(/_/g, ' ')}
                          </p>
                        </div>
                      )}
                      {listing.rentalDetails?.minLeaseTermMonths && (
                        <div className="p-5 border border-stone-200 bg-white">
                          <p className="text-xs uppercase tracking-widest text-stone-500 font-sans mb-2">
                            {pdListing?.minimumLease || 'Minimum Lease'}
                          </p>
                          <p className="text-base font-serif text-stone-900">
                            {listing.rentalDetails.minLeaseTermMonths}{' '}
                            {listing.rentalDetails.minLeaseTermMonths !== 1
                              ? (pdListing?.months || 'months')
                              : (pdListing?.month || 'month')}
                          </p>
                        </div>
                      )}
                      {listing.rentalDetails?.petPolicy &&
                        listing.rentalDetails.petPolicy !== 'NO_PETS' && (
                          <div className="p-5 border border-stone-200 bg-white">
                            <p className="text-xs uppercase tracking-widest text-stone-500 font-sans mb-2">
                              {pdListing?.petPolicy || 'Pet Policy'}
                            </p>
                            <p className="text-base font-serif text-stone-900 capitalize">
                              {listing.rentalDetails.petPolicy
                                .toLowerCase()
                                .replace(/_/g, ' ')}
                            </p>
                          </div>
                        )}
                      {listing.rentalDetails?.depositPrice?.amount && (
                        <div className="p-5 border border-stone-200 bg-white">
                          <p className="text-xs uppercase tracking-widest text-stone-500 font-sans mb-2">
                            {pdListing?.deposit || 'Deposit'}
                          </p>
                          <p className="text-base font-serif text-stone-900">
                            {listing.rentalDetails.depositPrice.currency}{' '}
                            {listing.rentalDetails.depositPrice.amount.toLocaleString()}
                          </p>
                        </div>
                      )}
                      {listing.availableDate && (
                        <div className="p-5 border border-stone-200 bg-white">
                          <p className="text-xs uppercase tracking-widest text-stone-500 font-sans mb-2">
                            {pdListing?.availableFrom || 'Available From'}
                          </p>
                          <p className="text-base font-serif text-stone-900">
                            {new Date(listing.availableDate).toLocaleDateString(
                              locale,
                              { dateStyle: 'long' },
                            )}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

            {/* Property Specifications */}
            {(property.condition ||
              property.aspect ||
              property.viewType ||
              property.titleType ||
              property.zoningCategory) && (
              <>
                <div className="border-t border-stone-200" />
                <div
                  className="
                    font-['Montserrat',-apple-system,BlinkMacSystemFont,'Segoe_UI',sans-serif]
                    [&_h2]:font-['Cormorant_Garamond','Georgia',serif]
                    [&_h3]:font-['Cormorant_Garamond','Georgia',serif]
                    **:rounded-none
                  "
                >
                  <PropertySpecifications
                    property={property}
                    messages={{
                      title:
                        pdSpecifications?.title ||
                        'Property Specifications',
                      subtitle:
                        pdSpecifications?.subtitle ||
                        'Detailed property information',
                    }}
                  />
                </div>
              </>
            )}

            {/* Amenities */}
            {amenities.length > 0 && (
              <>
                <div className="border-t border-stone-200" />
                <div data-track-section="amenities">
                  <StonePropertyAmenities
                    amenities={amenities}
                    messages={{
                      amenities: {
                        title:
                          pdAmenities?.title ||
                          'Amenities & Features',
                        subtitle:
                          pdAmenities?.subtitle ||
                          'What this property offers',
                        noAmenities:
                          pdAmenities?.noAmenities ||
                          'No amenities listed',
                      },
                    }}
                  />
                </div>
              </>
            )}

            {/* Virtual Tours */}
            {mediaByContext.VIRTUAL_TOUR &&
              mediaByContext.VIRTUAL_TOUR.length > 0 && (
                <>
                  <div className="border-t border-stone-200" />
                  <div
                    className="
                      font-['Montserrat',-apple-system,BlinkMacSystemFont,'Segoe_UI',sans-serif]
                      [&_h2]:font-['Cormorant_Garamond','Georgia',serif]
                      **:rounded-none
                    "
                  >
                    <PropertyVirtualTour tours={mediaByContext.VIRTUAL_TOUR} />
                  </div>
                </>
              )}

            {/* Videos */}
            {mediaByContext.VIDEO && mediaByContext.VIDEO.length > 0 && (
              <>
                <div className="border-t border-stone-200" />
                <div
                  className="
                    font-['Montserrat',-apple-system,BlinkMacSystemFont,'Segoe_UI',sans-serif]
                    [&_h2]:font-['Cormorant_Garamond','Georgia',serif]
                    **:rounded-none
                  "
                >
                  <PropertyVideos videos={mediaByContext.VIDEO} />
                </div>
              </>
            )}

            {/* Floor Plans */}
            {mediaByContext.FLOOR_PLAN &&
              mediaByContext.FLOOR_PLAN.length > 0 && (
                <>
                  <div className="border-t border-stone-200" />
                  <div
                    className="
                      font-['Montserrat',-apple-system,BlinkMacSystemFont,'Segoe_UI',sans-serif]
                      [&_h2]:font-['Cormorant_Garamond','Georgia',serif]
                      **:rounded-none
                    "
                  >
                    <PropertyFloorPlans
                      floorPlans={mediaByContext.FLOOR_PLAN}
                    />
                  </div>
                </>
              )}

            {/* Gallery */}
            {heroImages.length > 0 && (
              <>
                <div className="border-t border-stone-200" />
                <div data-track-section="gallery">
                  <StonePropertyGallery
                    media={heroImages}
                    floorPlans={floorPlansFromLinks}
                    models3D={[]}
                    propertyName={property.name}
                    messages={{
                      gallery: {
                        title:
                          pdGallery?.title || 'Gallery',
                        subtitle:
                          pdGallery?.subtitle ||
                          'Explore this property',
                        viewAll:
                          pdGallery?.viewAll ||
                          'View All Photos',
                        noImages:
                          pdGallery?.noImages ||
                          'No images available',
                      },
                    }}
                  />
                </div>
              </>
            )}

            {/* Valuations */}
            {valuations.length > 0 && (
              <>
                <div className="border-t border-stone-200" />
                <div
                  className="
                    font-['Montserrat',-apple-system,BlinkMacSystemFont,'Segoe_UI',sans-serif]
                    [&_h2]:font-['Cormorant_Garamond','Georgia',serif]
                    **:rounded-none
                  "
                >
                  <PropertyValuations valuations={valuations} locale={locale} />
                </div>
              </>
            )}

            {/* Documents */}
            {mediaByContext.DOCUMENT && mediaByContext.DOCUMENT.length > 0 && (
              <>
                <div className="border-t border-stone-200" />
                <div
                  className="
                    font-['Montserrat',-apple-system,BlinkMacSystemFont,'Segoe_UI',sans-serif]
                    [&_h2]:font-['Cormorant_Garamond','Georgia',serif]
                    **:rounded-none
                  "
                >
                  <PropertyDocuments documents={mediaByContext.DOCUMENT} />
                </div>
              </>
            )}

            {/* Location */}
            {(location?.latitude || location?.longitude) && (
              <>
                <div className="border-t border-stone-200" />
                <div data-track-section="location">
                  <StonePropertyLocation
                    address={propertyAddress}
                    city={propertyCity}
                    country={propertyCountry}
                    latitude={location?.latitude ?? 0}
                    longitude={location?.longitude ?? 0}
                    messages={{
                      location: {
                        title:
                          pdLocation?.title || 'Location',
                        subtitle:
                          pdLocation?.subtitle ||
                          "Where you'll find this property",
                        getDirections:
                          pdLocation?.getDirections ||
                          'Get Directions',
                        nearby:
                          pdLocation?.nearby ||
                          "What's nearby",
                      },
                    }}
                  />
                </div>
              </>
            )}
          </div>

          {/* Right Column - Contact Card */}
          <div className="lg:col-span-1">
            <div
              className="sticky top-32 bg-white p-8 border border-stone-200"
              data-track-section="contact"
            >
              {/* Agent Info */}
              {listingAgent && (
                <div className="flex items-center gap-4 mb-6 pb-6 border-b border-stone-200">
                  {listingAgent.avatar ? (
                    <Image
                      src={listingAgent.avatar}
                      alt={listingAgent.name}
                      width={56}
                      height={56}
                      className="w-14 h-14 object-cover"
                    />
                  ) : (
                    <div className="w-14 h-14 bg-stone-100 flex items-center justify-center">
                      <User size={24} className="text-stone-400" strokeWidth={1} />
                    </div>
                  )}
                  <div>
                    <p className="font-serif text-lg text-stone-900">
                      {listingAgent.name}
                    </p>
                    {listingAgent.jobTitle && (
                      <p className="text-xs uppercase tracking-widest text-stone-500 font-sans">
                        {listingAgent.jobTitle}
                      </p>
                    )}
                  </div>
                </div>
              )}

              <h3 className="font-serif text-2xl text-stone-900 mb-4">
                {pdContact?.interested || 'Interested?'}
              </h3>
              <p className="font-sans font-light text-stone-600 mb-6">
                {pdContact?.contactDescription || 'Contact us for more information or to arrange a private viewing.'}
              </p>

              <div className="mb-6">
                <button
                  onClick={() => {
                    analyticsEvents.contactProperty(property.id, property.name, 'request_info');
                    setShowEnquiryModal(true);
                  }}
                  className="flex items-center justify-center gap-2 w-full py-3 px-6 bg-stone-900 text-white text-xs uppercase tracking-widest font-sans hover:bg-stone-800 transition-colors"
                >
                  <Mail size={14} strokeWidth={1.5} />
                  {pdContact?.requestInfo || 'Request Information'}
                </button>
              </div>

              <div className="space-y-3 mb-6">
                <a
                  href={`mailto:${contact.email || 'p.monville@stoneinvestment.fr'}`}
                  onClick={() => analyticsEvents.clickToEmail(contact.email || '', `property_${property.id}`)}
                  className="flex items-center justify-center gap-2 w-full py-3 px-6 border border-stone-900 text-stone-900 text-xs uppercase tracking-widest font-sans hover:bg-stone-50 transition-colors"
                >
                  <Mail size={14} strokeWidth={1.5} />
                  {pdContact?.emailUs || 'Email Us'}
                </a>
                <a
                  href={`tel:${contact.phone || '+33 4 88 04 38 07'}`}
                  onClick={() => analyticsEvents.clickToCall(contact.phone || '', `property_${property.id}`)}
                  className="flex items-center justify-center gap-2 w-full py-3 px-6 border border-stone-900 text-stone-900 text-xs uppercase tracking-widest font-sans hover:bg-stone-50 transition-colors"
                >
                  <Phone size={14} strokeWidth={1.5} />
                  {pdContact?.callUs || 'Call Us'}
                </a>
                {whatsappNumber && (
                  <a
                    href={`https://wa.me/${whatsappNumber.replace(/[^0-9]/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => analyticsEvents.clickToWhatsApp(whatsappNumber, `property_${property.id}`)}
                    className="flex items-center justify-center gap-2 w-full py-3 px-6 border border-stone-300 text-stone-700 text-xs uppercase tracking-widest font-sans hover:bg-stone-50 transition-colors"
                  >
                    <MessageCircle size={14} strokeWidth={1.5} />
                    WhatsApp
                  </a>
                )}
              </div>

              {/* Schedule Meeting */}
              {listingAgent && (
                <div className="mb-6">
                  <button
                    onClick={() => {
                      analyticsEvents.scheduleMeetingIntent(listingAgent.name, `property_${property.id}`);
                      setShowMeetingModal(true);
                    }}
                    className="flex items-center justify-center gap-2 w-full py-3 px-6 border-2 border-stone-700 text-stone-800 text-xs uppercase tracking-widest font-sans hover:bg-stone-900 hover:text-white hover:border-stone-900 transition-colors"
                  >
                    <Calendar size={14} strokeWidth={1.5} />
                    {pdContact?.scheduleMeeting || 'Schedule a Meeting'}
                  </button>
                </div>
              )}

              {/* Save & Share */}
              <div className="grid grid-cols-2 gap-3 mb-6 pt-6 border-t border-stone-200">
                <div className="flex items-center justify-center">
                  <WishlistButton
                    entityType="PROPERTY"
                    entityId={property.id ?? ''}
                    entityName={property.name}
                    value={price ?? undefined}
                    size="md"
                    variant="outline"
                    showLabel
                    labels={{
                      save: pdContact?.saveProperty || 'Save',
                      saved: pdContact?.saved || 'Saved',
                    }}
                    className="rounded-none!"
                    authUrl={`/${locale}/register`}
                  />
                </div>
                <button
                  onClick={handleShare}
                  className="flex items-center justify-center gap-2 py-3 px-4 border border-stone-200 text-stone-700 text-xs uppercase tracking-widest font-sans hover:bg-stone-50 transition-colors"
                >
                  <Share2 size={14} strokeWidth={1.5} />
                  {pdContact?.shareProperty || 'Share'}
                </button>
              </div>

              <div className="text-xs text-stone-500 font-sans space-y-1">
                <p>{contact.email || 'p.monville@stoneinvestment.fr'}</p>
                <p>{contact.phone || '+33 4 88 04 38 07'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Agent Meeting Modal */}
      <AnimatePresence>
        {showMeetingModal && listingAgent && (
          <AgentMeetingModal
            agent={{
              id: listingAgent.id,
              name: listingAgent.name,
              image: listingAgent.avatar || '',
              email: listingAgent.email,
              ...(listingAgent.jobTitle ? { title: listingAgent.jobTitle } : {}),
            }}
            organisationId={tenant?.organisationId || ''}
            propertyPrice={price ?? undefined}
            onClose={() => setShowMeetingModal(false)}
          />
        )}
      </AnimatePresence>

      {/* Property Enquiry Modal — Stone-themed overrides */}
      <AnimatePresence>
        {showEnquiryModal && (
          <div
            className="
              font-['Montserrat',-apple-system,BlinkMacSystemFont,'Segoe_UI',sans-serif]
              [&_h3]:font-['Cormorant_Garamond','Georgia',serif]
              [&_h4]:font-['Cormorant_Garamond','Georgia',serif]
              [&_.enquiry-modal-panel]:rounded-none
              [&_.enquiry-modal-content]:rounded-none
              [&_.enquiry-modal-input]:rounded-none
              [&_.enquiry-modal-submit]:rounded-none
              [&_.enquiry-modal-icon]:rounded-none
            "
            style={{
              '--enquiry-primary': '#1c1917',
              '--enquiry-primary-dark': '#0c0a09',
              '--enquiry-accent': '#57534e',
              '--enquiry-accent-light': '#a8a29e',
            } as React.CSSProperties}
          >
            <PropertyEnquiryModal
              propertyId={property.id}
              propertyName={property.name}
              propertyPrice={price ?? undefined}
              intent="buyer"
              onClose={() => setShowEnquiryModal(false)}
            />
          </div>
        )}
      </AnimatePresence>
    </main>
  );
}
